import {useCallback, useMemo, useState} from 'react';

import {
  Button,
  CountryPicker,
  Input,
  Modal,
  Select,
  SpinningLoader,
} from '@/components';
import {TextArea} from '@/components/common/Input';
import type {BlockedPaymentMethod} from '@/services/blockedPaymentMethodService';
import type {AfriexCountryCode} from '@/types';
import {AfriexTransactionChannel} from '@/types';

import type {BlockedPaymentMethodModalProps} from './types';
import {
  transactionTypeToIdentifier,
  transactionTypeToProvider,
  transationTypeToLabel,
} from './types';

const BlockedPaymentMethodModal = ({
  onClose,
  handleSave,
  showModal,
}: BlockedPaymentMethodModalProps) => {
  const [formValues, setFormValues] = useState<BlockedPaymentMethod>(
    {} as BlockedPaymentMethod,
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const label = useMemo(() => {
    return transationTypeToLabel?.[formValues.type] || 'Payment Method';
  }, [formValues]);

  const providerLabel = useMemo(() => {
    return transactionTypeToProvider?.[formValues.type] || 'Provider';
  }, [formValues]);

  // Same dependency as the two sibling memos above: the body reads
  // `formValues.type` off `formValues`, so `formValues` is the honest
  // dependency. The value is a lookup in a constant map keyed by `type`, so
  // recomputing on any `formValues` change returns the same string.
  const identifierLabel = useMemo(() => {
    return (
      transactionTypeToIdentifier?.[formValues.type] || 'Account Identifier'
    );
  }, [formValues]);

  const transactionChannelOptions = [
    {
      key: 'Debit Card',
      value: AfriexTransactionChannel.CARD,
    },
    {
      key: 'Bank Account',
      value: AfriexTransactionChannel.BANK_ACCOUNT,
    },
    {
      key: 'Mobile Money',
      value: AfriexTransactionChannel.MOBILE_MONEY,
    },
  ];

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    await handleSave(formValues);
    setIsLoading(false);
  }, [formValues, handleSave]);

  return (
    <Modal
      isOpen={showModal}
      onClose={onClose}
      width="w-11/12 md:w-5/12 lg:w-4/12 xl:w-3/12 px-4 sm:px-0"
      title={`Add ${label} To Blocked List`}
      closeOnOutsideClick
    >
      <div className="mx-3">
        {isLoading ? (
          <div className="flex w-full h-full justify-center items-center my-5">
            <SpinningLoader />
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="flex flex-col space-y-5">
              <div className="mt-5">
                <CountryPicker
                  label="Country"
                  // `CountryPicker` and `Select` spread their props onto a
                  // native <select>, but `SelectProps` carries an
                  // `[key: string]: any` index signature, so the handler
                  // argument has to be annotated to be typed at all. The
                  // options are `AfriexCountry.iso2` values, and the
                  // placeholder <option> is disabled, so the `|| ''` fallback
                  // is unreachable.
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setFormValues((x) => ({
                      ...x,
                      country: (e?.target?.value || '') as AfriexCountryCode,
                    }))
                  }
                  value={formValues.country}
                  name="country"
                  placeholder="Select One"
                />
              </div>

              <div className="mt-5">
                <Select
                  label="Transaction Channel"
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setFormValues((x) => ({
                      ...x,
                      type: (e?.target?.value ||
                        '') as AfriexTransactionChannel,
                    }))
                  }
                  value={formValues.type}
                  name="type"
                  options={transactionChannelOptions}
                  placeholder="Select One"
                />
              </div>
              <div className="mt-5">
                <Input
                  className={'w-full'}
                  type="text"
                  label={providerLabel}
                  onChange={(e) =>
                    setFormValues((x) => ({
                      ...x,
                      provider: e?.target?.value || '',
                    }))
                  }
                />
              </div>

              <div className="mt-5">
                <Input
                  className={'w-full'}
                  type="text"
                  label={identifierLabel}
                  onChange={(e) =>
                    setFormValues((x) => ({
                      ...x,
                      accountIdentifier: e?.target?.value || '',
                    }))
                  }
                />
              </div>

              <div className="mt-5">
                <TextArea
                  label="Reason"
                  onChange={(e) =>
                    setFormValues((x) => ({
                      ...x,
                      reason: e?.target?.value || '',
                    }))
                  }
                />
              </div>

              <div className="flex w-full justify-end space-x-2 py-5">
                <Button onClick={onClose} variant="outline" colorScheme="cyan">
                  Cancel
                </Button>
                <Button disabled={isLoading} type="submit" variant="solid">
                  Submit
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </Modal>
  );
};

export default BlockedPaymentMethodModal;
